import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Snackbar, IconButton } from "@mui/material";
import ConfiguratorForm from "../Components/ConfigurationsSteps/ConfiguratorForm";
import ConfiguratorMenu from "../Components/Stepper/ConfiguratorMenu";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  fetchPatterns,
  fetchBearerToken,
  fetchAllTextures,
  fetchPrice,
} from "../helpers/DataRequests";
import { fetchMessageRules } from "../helpers/MessageRulesApi";
import {
  normalizeMasterDimFormulas,
  normalizeCacheDimMinimums,
} from "../helpers/evalCacheDimFormula";
import { resolveDecValues } from "../helpers/decHelpers";
import CircularProgress from "@mui/material/CircularProgress";
import ShareIcon from "@mui/icons-material/Share";
import ThreeScene from "../Components/ThreeJs/ThreeScene";
import { VIEWER_TOP_OFFSET_PX, VIEWER_OVERLAY_BOTTOM_PX } from "../Components/ThreeJs/viewerLayout";
import {
  buildPriceFormulaVars,
  isProductSelectionDebugAllowed,
} from "../helpers/productSelectionDebug";
import {
  applyShareParamsToMaterialDefaults,
  applyShareParamsToModelDefaults,
  buildShareSearchParams,
  shouldDeferPriceForShareUrl,
} from "../helpers/shareUrlParams";

const DEFAULT_MODEL_FORM_DATA = {
  pattern: null,
  patternName: "",
  type: "",
  wallmount: true,
  sizeX: 890,
  sizeY: 667,
  sizeZ: 480,
  sizeL: 800,
  sizeH: 620,
  sizeP1: 400,
  sizeP2: 430,
  frontHeight: 650,
  bottom: false,
  rear: false,
  aModifierDimensionsCache: false,
  cacheManualWidth: false,
  cacheManualDepth: false,
  cacheManualRearY: false,
  aVuLesDimensionsCache: false,
  aValiderAvecUnMessageDalerte: false,
};

const DEFAULT_MATERIAL_FORM_DATA = {
  material: null,
  materialName: "",
  materialType: "",
};

const formatTexturesLocal = (txs) => {
  const colors = { brillants: [], mats: [], 'métal': [], bois: [] };
  (txs || []).forEach((tx) => { if (colors[tx.materialType]) colors[tx.materialType].push(tx); });
  return [
    { id: "brillants", name: "Brillant", colors: colors.brillants },
    { id: "mats", name: "Mat", colors: colors.mats },
    { id: "metal", name: "Métal", colors: colors["métal"] },
    { id: "bois", name: "Bois", colors: colors.bois },
  ];
};

/** Valeurs par défaut à l'arrivée sur le configurateur (sans params URL). */
const DEFAULT_PATTERN_ID = "72"; // Occitan
const DEFAULT_MATERIAL_ID = "17"; // Ivoire mat

const Configurator = ({ isPro = false, coefMarge }) => {
  // Debug log déplacé dans useEffect pour voir les vrais mounts

  const [modelFormData, setModelFormData] = useState(() =>
    applyShareParamsToModelDefaults(
      DEFAULT_MODEL_FORM_DATA,
      typeof window !== "undefined" ? window.location.search : "",
    ),
  );
  const [materialFormData, setMaterialFormData] = useState(() =>
    applyShareParamsToMaterialDefaults(
      DEFAULT_MATERIAL_FORM_DATA,
      typeof window !== "undefined" ? window.location.search : "",
    ),
  );

  // Formules maîtresses « dimensions d'installation → cache » chargées
  // depuis GlobalSettings.cache_dim_formulas (clé exposée publiquement par
  // /message-rules/public). Préchargées au montage pour que les premiers
  // recalculs (étape 3 sélection du type, étape 4 saisie L/H/P1/P2) puissent
  // s'aligner exactement sur le BO, AVANT même qu'un produit ne soit
  // sélectionné. Quand le produit est chargé ensuite, son champ
  // `dimFormulas` (renvoyé par /products/:id/config) doit être strictement
  // identique (le backend lit la même source) — on garde malgré tout
  // l'override produit pour rétro-compat.
  const [dimFormulas, setDimFormulas] = useState(null);
  // Seuils minimums durs du cache (GlobalSettings.cache_dim_minimums), éditables
  // depuis /products. Null tant que non chargé → ConfiguratorForm retombe sur
  // les valeurs par défaut.
  const [cacheMinimumsConfig, setCacheMinimumsConfig] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [priceFormulaVars, setPriceFormulaVars] = useState(null);
  const [priceSurfaceParts, setPriceSurfaceParts] = useState(null);
  const priceRequestIdRef = useRef(0);
  const deferPriceForShareUrlRef = useRef(
    typeof window !== "undefined" && shouldDeferPriceForShareUrl(window.location.search),
  );
  // DEC produit (Products.dec) du produit actuellement sélectionné, propagé
  // depuis FacesLoaderDynamic via handleProductConfigLoaded. Exposé dans les
  // formules sous DECc (DECi est dérivé du type d'installation par
  // resolveDecValues). Null si pas de produit sélectionné ou pas de dec
  // configuré sur le produit.
  const [productDec, setProductDec] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { settings } = await fetchMessageRules();
        if (cancelled) return;
        const raw = settings?.cache_dim_formulas;
        if (raw != null) {
          setDimFormulas(normalizeMasterDimFormulas(raw));
        }
        if (settings?.cache_dim_minimums != null) {
          setCacheMinimumsConfig(
            normalizeCacheDimMinimums(settings.cache_dim_minimums)
          );
        }
      } catch (e) {
        console.warn('[Configurator] Erreur chargement cache_dim_formulas:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleProductConfigLoaded = useCallback((config) => {
    // Override par les formules retournées avec le produit, qui proviennent
    // du même GlobalSettings côté backend. Si elles sont vides/absentes, on
    // garde celles déjà préchargées au montage.
    if (config?.dimFormulas) {
      setDimFormulas(config.dimFormulas);
    }
    setSelectedProductId(config?.id || null);
    // DEC produit (Products.dec) — propagé jusqu'aux formules sous DECc.
    setProductDec(config?.dec ?? null);
  }, []);

  const [quotationData, setQuotationData] = useState({
    isNeeded: false,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [price, setPrice] = useState(0);
  const [bearer, setBearer] = useState("");
  const [patterns, setPatterns] = useState([]);
  const [textures, setTextures] = useState([]);
  const [allTextures, setAllTextures] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isButtonCartDisabled, setIsButtonCartDisabled] = useState(true);
  const [hidePrice, setHidePrice] = useState(false);
  const [hide3D, setHide3D] = useState(false);
  const [hasMatchingProduct, setHasMatchingProduct] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchAndUpdateToken();
  }, []);

  useEffect(() => {
    if (!!bearer && bearer.length > 0) {
      fetchPatternsAndTextures();
    }
  }, [bearer]);

  const fetchPatternsAndTextures = async () => {
    const [fetchedPatterns, fetchedAllTextures] = await Promise.all([
      fetchPatterns(bearer),
      fetchAllTextures(bearer),
    ]);
    
    const fetchedTextures = formatTexturesLocal(fetchedAllTextures || []);
    
    setPatterns(fetchedPatterns);
    setTextures(fetchedTextures);
    setAllTextures(fetchedAllTextures);

    // Motif : défaut ou resynchronisation du libellé depuis l'id (URL / partage).
    setModelFormData((prev) => {
      if (!fetchedPatterns?.length) return prev;

      if (prev.pattern) {
        const found = fetchedPatterns.find(
          (p) => String(p.id) === String(prev.pattern),
        );
        if (found && found.name !== prev.patternName) {
          return { ...prev, patternName: found.name };
        }
        return prev;
      }

      const defaultPattern =
        fetchedPatterns.find((p) => String(p.id) === DEFAULT_PATTERN_ID)
        || fetchedPatterns.find((p) => p.isDefault)
        || fetchedPatterns[0];
      return {
        ...prev,
        pattern: String(defaultPattern.id),
        patternName: defaultPattern.name,
      };
    });

    setMaterialFormData((prev) => {
      if (!fetchedAllTextures?.length) return prev;

      if (prev.material) {
        const found = fetchedAllTextures.find(
          (m) => String(m.id) === String(prev.material),
        );
        if (found) {
          return {
            ...prev,
            material: String(found.id),
            materialName: found.name,
            materialType: found.materialType,
            color: found,
          };
        }
        return prev;
      }

      const defaultMaterial =
        fetchedAllTextures.find((m) => String(m.id) === DEFAULT_MATERIAL_ID)
        || fetchedAllTextures.find((m) => m.isDefault)
        || fetchedAllTextures[0];
      return {
        ...prev,
        material: String(defaultMaterial.id),
        materialName: defaultMaterial.name,
        materialType: defaultMaterial.materialType,
        color: defaultMaterial,
      };
    });

    // Data is now ready
    setIsDataReady(true);
  };
  const setupPrice = useCallback(async (
    overrideX = null,
    overrideY = null,
    overrideZ = null
  ) => {
    if (!materialFormData.material || !bearer) return;

    const requestId = ++priceRequestIdRef.current;

    const installType = modelFormData.type || '';
    const { DEC, DECi, DECc } = resolveDecValues(installType, productDec);
    const priceSizeX = overrideX ? Number(overrideX).toFixed(0) : modelFormData.sizeX.toFixed(0);
    const priceSizeY = overrideY
      ? Number(overrideY).toFixed(0)
      : modelFormData.frontHeight.toFixed(0);
    const priceSizeZ = overrideZ ? Number(overrideZ).toFixed(0) : modelFormData.sizeZ.toFixed(0);
    const installDims = {
      L: Number(modelFormData.sizeL) || 0,
      H: Number(modelFormData.sizeH) || 0,
      P1: Number(modelFormData.sizeP1) || 0,
      P2: Number(modelFormData.sizeP2) || 0,
      DEC,
      DECi,
      DECc,
      INSTALL: installType,
      WALLMOUNT: !!modelFormData.wallmount,
      BOTTOM: !!modelFormData.bottom,
      REAR: !!modelFormData.rear,
    };
    const debugPrice = isProductSelectionDebugAllowed();
    const mat = allTextures.find(
      (t) => String(t.id) === String(materialFormData.material),
    );
    const materialFactor = mat?.priceFactor ?? 1;

    const priceResult = await fetchPrice(
      bearer,
      priceSizeX,
      priceSizeY,
      priceSizeZ,
      modelFormData.bottom,
      materialFormData.material,
      selectedProductId,
      installDims,
      { includeSurface: debugPrice },
    );

    if (requestId !== priceRequestIdRef.current) return;
    if (priceResult == null) return;

    const priceTtc =
      typeof priceResult === 'object' && priceResult != null && 'price' in priceResult
        ? priceResult.price
        : priceResult;
    const surface =
      typeof priceResult === 'object' && priceResult != null
        ? priceResult.surface
        : null;
    const surfaceUnit =
      typeof priceResult === 'object' && priceResult != null
        ? priceResult.surfaceUnit
        : null;
    const surfaceParts =
      typeof priceResult === 'object' && priceResult != null
        ? priceResult.surfaceParts
        : null;

    if (debugPrice) {
      setPriceFormulaVars(
        buildPriceFormulaVars({
          sizeX: priceSizeX,
          sizeY: priceSizeY,
          sizeZ: priceSizeZ,
          installDims,
          surface,
          surfaceUnit,
          materialFactor,
        }),
      );
      setPriceSurfaceParts(Array.isArray(surfaceParts) ? surfaceParts : null);
    }

    let finalPrice = Number(priceTtc);
    if (isPro && coefMarge) {
      finalPrice *= parseFloat(coefMarge);
    }

    setPrice(finalPrice);
  }, [
    bearer,
    materialFormData.material,
    modelFormData,
    productDec,
    selectedProductId,
    allTextures,
    isPro,
    coefMarge,
  ]);

  const fetchAndUpdateToken = async () => {
    setBearer(await fetchBearerToken());
  };

  useEffect(() => {
    let { pattern, sizeX, sizeY, sizeZ, bottom, type, frontHeight } =
      modelFormData;
    const { material } = materialFormData;
    
    // 🆕 SUPPRIMÉ: Les limites de dimensions hardcodées ont été retirées
    // Le système de produits dynamiques gère maintenant les dimensions max via les règles d'affichage
    // Les produits "double hauteur" et "double largeur" peuvent avoir des dimensions plus grandes
    setHide3D(false);

    if (modelFormData.sizeX < 310) {
      sizeX = 310;
    }

    if (modelFormData.sizeY < 250) {
      sizeY = 250;
    }

    if (modelFormData.sizeZ < 250) {
      sizeZ = 250;
    }

    // Lien de partage : éviter un prix intermédiaire (formule globale / mauvaises
    // dimensions) avant que le produit 3D soit résolu et le cache recalculé.
    if (deferPriceForShareUrlRef.current && !selectedProductId) {
      return;
    }

    setupPrice(sizeX.toFixed(0), frontHeight.toFixed(0), sizeZ.toFixed(0));
    // Recalcul aussi quand le produit chargé change (SURFACE DXF + priceFormula).
  }, [modelFormData, materialFormData.material, selectedProductId, productDec, bearer, setupPrice]);

  const handleModelInfosChange = (key, value) => {
    setModelFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleMetadataInfosChange = (key, value) => {
    setModelFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleMaterialInfosChange = (key, value) => {
    const color = allTextures.find((c) => c.id === value);
    setMaterialFormData((prev) => ({
      ...prev,
      [key]: value,
      materialType: color.materialType,
      materialName: color.name,
      color: color,
    }));
  };
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const handleStepChange = (newStep) => {
    if (!modelFormData.type) {
      setIsButtonCartDisabled(true);
    } else {
      setIsButtonCartDisabled(false);
    }
    if (isXs && newStep === 2 && !modelFormData.type) {
      setSnackbarMessage(
        "Veuillez sélectionner un type de pose avant de passer à l'étape des dimensions."
      );
      setSnackbarOpen(true);
      setCurrentStep(1);
      return;
    } else if (newStep === 3 && !modelFormData.type) {
      setSnackbarMessage(
        "Veuillez sélectionner un type de pose avant de passer à l'étape des dimensions."
      );
      setSnackbarOpen(true);
      setCurrentStep(2);
      return;
    }
    setCurrentStep(newStep);
  };

  const handleQuotationInfosChange = (key, value) => {
    if (key === "noPrice") {
      setHidePrice(value);
    }
    setQuotationData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const generateShareableURL = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    const params = buildShareSearchParams(modelFormData, materialFormData);
    return `${baseUrl}?${params.toString()}`;
  };

  const handleShareCache = async () => {
    const url = generateShareableURL();
    try {
      await navigator.clipboard.writeText(url);
      setSnackbarMessage("Lien copié dans le presse-papiers !");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Échec de la copie : ", err);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const [isLoading, setIsLoading] = useState(false);
  const cartInFlightRef = useRef(false);

  // Retour navigateur (bfcache) : la page peut être restaurée avec isLoading=true
  // après une redirection panier réussie.
  useEffect(() => {
    const resetCartLoading = () => {
      cartInFlightRef.current = false;
      setIsLoading(false);
    };

    const resetIfReturningFromCart = () => {
      try {
        if (sessionStorage.getItem("usipanel.cartRedirect")) {
          sessionStorage.removeItem("usipanel.cartRedirect");
          resetCartLoading();
        }
      } catch {
        /* ignore */
      }
    };

    resetIfReturningFromCart();

    const onPageShow = (event) => {
      if (event.persisted) {
        resetCartLoading();
      }
      resetIfReturningFromCart();
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (cartInFlightRef.current) return;

    cartInFlightRef.current = true;
    setIsLoading(true);
    let redirecting = false;
    let { sizeX, sizeY, sizeZ } = modelFormData;

    if (modelFormData.sizeX < 310) {
      sizeX = 310;
    }

    if (modelFormData.sizeY < 250) {
      sizeY = 250;
    }

    if (modelFormData.sizeZ < 250) {
      sizeZ = 250;
    }

    try {
      // 🆕 Mode direct : bypasser WooCommerce et créer la commande directement
      const isDirectOrderMode = process.env.REACT_APP_DIRECT_ORDER === 'true';
      
      if (isDirectOrderMode) {
        // Générer un numéro de commande unique (format TEST-timestamp)
        const orderNumber = `99999`;
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/orders/create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer )k2@kqLR&@Xmd8Pk", // Token fixe pour /orders/create
            },
            body: JSON.stringify({
              orderNumber,
              products: [{
                // Mapping des dimensions: Renderer → Backend
                // Renderer: sizeX=largeur, sizeY=hauteur arrière, sizeZ=profondeur, frontHeight=hauteur avant
                // Backend:  sizeX=profondeur, sizeY=largeur, sizeZ=hauteur (AVANT, affichée sur le cache)
                sizeX: sizeZ,  // Backend profondeur ← Renderer sizeZ (profondeur)
                sizeY: sizeX,  // Backend largeur ← Renderer sizeX (largeur)
                sizeZ: modelFormData.frontHeight,  // Backend hauteur ← Renderer frontHeight (hauteur AVANT, affichée sur le cache)
                patternId: modelFormData.pattern,
                material: materialFormData.material,
                bottom: modelFormData.bottom,
                sizeL: modelFormData.sizeL,
                sizeH: modelFormData.sizeH,
                sizeP1: modelFormData.sizeP1,
                sizeP2: modelFormData.sizeP2,
                frontHeight: modelFormData.frontHeight,
                rearHeight: sizeY,  // 🆕 Ajouter la hauteur arrière pour référence
                // ⚠️ `type` ici = type de materiau (Inox brosse, ...), garde pour
                // retro-compat. Le TYPE d'INSTALLATION (sol/sol-mur/mur/
                // mur-haut) est envoye separement via `installType` pour que
                // selectProductByDimensions cote backend puisse evaluer
                // correctement les displayExpression qui referencent
                // WALLMOUNT/BOTTOM/HTOP/REAR/TYPE/DEC.
                type: materialFormData.materialType,
                installType: modelFormData.type || '',
                quantity: 1,
              }],
            }),
          }
        );

        const data = await response.json();
        if (response.ok) {
          setSnackbarMessage(`✅ Commande créée ! N° ${orderNumber}`);
          setSnackbarOpen(true);
        } else {
          console.error("Erreur création commande:", data);
          setSnackbarMessage(`❌ Erreur: ${data.error || data.message}`);
          setSnackbarOpen(true);
        }
      } else {
        // Mode normal : passer par WooCommerce
        const bearer = await fetchBearerToken();
        if (!bearer) {
          setSnackbarMessage("❌ Impossible de contacter le serveur");
          setSnackbarOpen(true);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/validate-order`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${bearer}`,
            },
            body: JSON.stringify({
              sizeX: sizeX,
              sizeY: sizeY,
              sizeZ: sizeZ,
              pattern: modelFormData.pattern,
              material: materialFormData.material,
              bottom: modelFormData.bottom,
              rear: modelFormData.rear,
              wallmount: modelFormData.wallmount,
              sizeL: modelFormData.sizeL,
              sizeH: modelFormData.sizeH,
              sizeP1: modelFormData.sizeP1,
              sizeP2: modelFormData.sizeP2,
              frontHeight: modelFormData.frontHeight,
              type: materialFormData.materialType,
              installType: modelFormData.type || '',
              productId: selectedProductId,
              productDec,
              displayPrice: price,
              aModifierDimensionsCache: modelFormData.aModifierDimensionsCache,
              aVuLesDimensionsCache: modelFormData.aVuLesDimensionsCache,
              aValiderAvecUnMessageDalerte:
                modelFormData.aValiderAvecUnMessageDalerte,
              isPro: isPro,
              coefMarge: coefMarge,
            }),
          }
        );

        const data = await response.json();
        if (response.ok) {
          redirecting = true;
          try {
            sessionStorage.setItem("usipanel.cartRedirect", "1");
          } catch {
            /* ignore */
          }
          window.location.href = data.cartUrl;
        } else {
          console.error("Erreur :", data.message);
          setSnackbarMessage(`❌ Erreur: ${data.message || "ajout au panier"}`);
          setSnackbarOpen(true);
        }
      }
    } catch (err) {
      console.error("Erreur réseau :", err.message);
      setSnackbarMessage(`❌ Erreur réseau: ${err.message}`);
      setSnackbarOpen(true);
    } finally {
      if (!redirecting) {
        cartInFlightRef.current = false;
        setIsLoading(false);
      }
    }
  }, [modelFormData, materialFormData, isPro, coefMarge, selectedProductId, productDec, price]);

  useEffect(() => {
    window.handleAddToCart = handleAddToCart;
  }, [handleAddToCart]);

  // ⚠️ SUPPRIMÉ: setupPrice() était appelé directement dans le render, causant des re-renders infinis !
  // setupPrice() est déjà appelé via le useEffect qui dépend de modelFormData

  // 🆕 Mode direct : bypasser WooCommerce
  const isDirectOrderMode = process.env.REACT_APP_DIRECT_ORDER === 'true';

  return (
    <>
      {/* 🆕 Indicateur mode direct (dev/test) */}
      {isDirectOrderMode && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "#ff9800",
            color: "white",
            textAlign: "center",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 9999,
          }}
        >
          ⚡ MODE DIRECT ACTIVÉ - Les commandes sont créées sans passer par WooCommerce
        </Box>
      )}

      <ConfiguratorMenu
        price={price}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        quotationDatas={quotationData}
        isButtonCartDisabled={isButtonCartDisabled || isLoading}
        hidePrice={hidePrice}
        modelFormData={modelFormData}
        materialFormData={materialFormData}
        handleAddToCart={handleAddToCart}
      />

      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            zIndex: 2000,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Nous créons votre cache dans notre système…
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          pt: { xs: 2, md: 8 },
          position: "relative",
        }}
      >
        {/* 3D Model Viewer */}
        <Box
          sx={{
            display: isMobile && currentStep === 1 ? "none" : "flex",
            flexDirection: "column",
            alignItems: "center",
            width: { xs: "100%", md: "50vw" },
            height: { xs: "32vh", sm: "unset" },
          }}
        >
          {/* Three.js */}
          <Box
            sx={{
              width: "100%",
              height: { xs: "40vh", sm: "50vh", md: "60vh" },
              backgroundColor: "#ffffff",
              position: { xs: "relative", md: "sticky" },
              bottom: { xs: "8vh", md: "unset" },
              top: { xs: 100, md: VIEWER_TOP_OFFSET_PX },
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Nota installation type — affiche un rappel pendant les étapes
                qui précèdent la sélection du type d'installation (TypeForm),
                pour que le client ne pense pas que la configuration par
                défaut (placeholders + dimensions du cache) est figée.
                  - Desktop : TypeForm = étape 3 (currentStep === 2).
                  - Mobile  : TypeForm = étape 2 (currentStep === 1) et la
                    3D est de toute façon masquée à ce moment, donc le nota
                    ne s'affiche que sur l'étape 1 (currentStep === 0). */}
            {currentStep < (isMobile ? 1 : 2) && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: { xs: 8, md: 16 },
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 4,
                  pointerEvents: "none",
                  maxWidth: { xs: "92%", md: "85%" },
                  textAlign: "center",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "999px",
                  backgroundColor: "rgba(92, 177, 226, 0.10)",
                  border: "1px solid rgba(92, 177, 226, 0.35)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#3a7fa8",
                    fontWeight: 500,
                    fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                    lineHeight: 1.3,
                  }}
                >
                  Vous pourrez modifier le type d&apos;installation à l&apos;étape {isMobile ? 2 : 3}.
                </Typography>
              </Box>
            )}
            {isDataReady ? (
            <ThreeScene
              hide3D={hide3D}
              noMatchingProduct={hasMatchingProduct === false}
              modelFormData={modelFormData}
              materialFormData={materialFormData}
              allTextures={allTextures}
              isMobile={isMobile}
              onProductConfigLoaded={handleProductConfigLoaded}
              priceDebug={{
                apiProductId: selectedProductId,
                displayPriceTtc: price,
                formulaVars: priceFormulaVars,
                surfaceParts: priceSurfaceParts,
              }}
              sx={{
                width: "100%",
                height: { xs: "40vh", sm: "50vh", md: "60vh" },
                backgroundColor: "#ffffff",
                position: { md: "sticky" },
                top: { xs: 0, md: VIEWER_TOP_OFFSET_PX },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: VIEWER_OVERLAY_BOTTOM_PX,
                  left: 0,
                  width: "100%",
                  height: "20%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 16px",
                  zIndex: 3,
                }}
              >
                {!isPro && (
                  <>
                    {/* Fabriqué en France */}
                    {!isMobile && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={`${process.env.PUBLIC_URL}/images/Infos/france-coq.png`}
                          alt="Fabriqué en France"
                          sx={{
                            width: { xs: 30, sm: 40, md: 50 },
                            height: { xs: 30, sm: 40, md: 50 },
                            mb: 1,
                          }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.875rem",
                              md: "1rem",
                            },
                          }}
                        >
                          Fabriqué en France
                        </Typography>
                      </Box>
                    )}
                    {isMobile && <Box sx={{ flexGrow: 1 }} />}
                    <Box>
                      <IconButton
                        onClick={handleShareCache}
                        sx={{
                          backgroundColor: "#efefef",
                          color: "#5CB1E2",
                          border: "1px solid transparent",
                          marginTop: { xs: "15px", md: 0 },
                          "&:hover": {
                            backgroundColor: "#efefef",
                            border: "1px solid #5CB1E2",
                          },
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>
            </ThreeScene>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: "50vw" },
            padding: { xs: "10px", md: "0px" },
            paddingBottom: "30px",
          }}
        >
          <ConfiguratorForm
            step={currentStep}
            patterns={patterns}
            textures={textures}
            modelFormData={modelFormData}
            materialFormData={materialFormData}
            quotationData={quotationData}
            onModelFormChange={handleModelInfosChange}
            onMetadataInfosChange={handleMetadataInfosChange}
            onMaterialFormChange={handleMaterialInfosChange}
            onQuotationChange={handleQuotationInfosChange}
            handleStepChange={handleStepChange}
            quotationDatas={quotationData}
            isButtonCartDisabled={isButtonCartDisabled || isLoading}
            hidePrice={hidePrice}
            handleAddToCart={handleAddToCart}
            price={price}
            dimFormulas={dimFormulas}
            cacheMinimumsConfig={cacheMinimumsConfig}
            productDec={productDec}
            onHasMatchingProductChange={setHasMatchingProduct}
          />
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          zIndex: 1200,
          paddingBottom: { xs: "7vh", md: "5vh" },
        }}
      />
    </>
  );
};

export default Configurator;
